/**
 * AI CONTEXT
 * Purpose: left panel in TeacherView; lists teacher sessions and allows selecting one.
 * Inputs: TeacherViewContext (sessions, summaries, selectedDate/session).
 * Used by: TeacherView splitter layout.
 * Importance: controls which session drives attendance + class detail panels.
 * Refactor: extract session-card item component to reduce rendering complexity.
 */
import { AnimatePresence } from "motion/react"
import { useState } from "react"
import styled from "styled-components"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import ComponentErrorBoundary from "#blocks/ComponentErrorBoundary"
import DateDisplay from "#blocks/DateDisplay"
import EmptyState from "#blocks/EmptyState"
import Flex from "#blocks/Flex"
import {
  type ThemeContextType,
  useThemeContext,
} from "#contexts/ThemeContextProvider"
import AttendanceViewSessionSummary, {
  IconsRow,
} from "#views/AdminView/AttendanceView/AttendanceViewSessionSummary"

import { useTeacherViewContext } from "./TeacherViewContextProvider"

const SHighlight = styled.div<{ selected: boolean; theme: ThemeContextType }>`
  cursor: pointer;
  background-color: ${({ selected, theme }) =>
    selected ? theme.highlight : "transparent"};
  padding: 20px 36px;
  transition: all 0.15s ease-in-out;

  &:hover {
    padding-left: 40px;
    padding-right: 32px;
  }
`

const TeacherSchedule = () => {
  const theme = useThemeContext()
  const { white, border, schedule } = theme
  const [isLoading, setIsLoading] = useState(true)

  const {
    allSessionAttendanceSummary,
    sessionsForCurrentUser,

    allSessionStudentTypeSummaries,

    selectedSession,
    setSelectedSessionId,

    selectedDate,
    setSelectedDate,
  } = useTeacherViewContext()

  return (
    <Flex
      style={{
        width: "100%",
        height: "100%",
        backgroundColor: schedule,
        overflowY: "auto",
      }}
      col
    >
      <Flex
        col
        style={{
          backgroundColor: white,
          borderBottom: `2px solid ${border}`,
          padding: "30px 40px 30px 57px",
        }}
        gap={18}
      >
        <DateDisplay
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
        />
        <IconsRow
          isLoading={isLoading}
          studentTypeSummary={allSessionStudentTypeSummaries}
          attendanceSummary={allSessionAttendanceSummary}
        />
      </Flex>

      <Flex style={{ padding: "20px 0", background: schedule }} col>
        <AnimatePresence mode="sync">
          {sessionsForCurrentUser.length > 0 ? (
            sessionsForCurrentUser.map((session) => (
              <ComponentErrorBoundary
                key={session.id}
                context={`Class: ${session.name}`}
                fallbackStyle={{
                  width: 350,
                  minHeight: 120,
                  margin: "20px 36px",
                }}
              >
                <AnimateInFlex
                  animation={ANIMATION_BY_KEY.leftSm}
                  onAnimationComplete={() => setIsLoading(false)}
                >
                  <SHighlight
                    selected={selectedSession?.id === session.id}
                    theme={theme}
                    onClick={() => setSelectedSessionId(session.session.id)}
                  >
                    <AttendanceViewSessionSummary adminViewSession={session} />
                  </SHighlight>
                </AnimateInFlex>
              </ComponentErrorBoundary>
            ))
          ) : (
            <EmptyState description="No sessions found" />
          )}
        </AnimatePresence>
      </Flex>
    </Flex>
  )
}

export default TeacherSchedule
